import { useForm } from 'react-hook-form';
import { Form } from 'semantic-ui-react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { SkupremeModal } from '../../components';
import {
  ColoredPill,
  ControlledTextFormField,
  DropdownFormField,
  HoverBorderButton,
  StyledButton,
} from '../../elements';
import { WarehouseShipnodeHook } from '../../hooks';
import { CommonUtility, theme } from '../../utility';
import { normalizeShipnodeEntries } from '../../utility/shipnode/normalize';

const WarehouseSchema = yup.object().shape({
  stationName: yup.string().required('Station Name is required'),
  computerId: yup.string().required('Computer is required'),
  printerId: yup.string().required('Printer is required'),
});

export function AddPackingStations({
  warehouseId,
  savePacking,
  saving,
  open,
  onClose,
  existingShipnodes,
}) {
  const { data: shipnodeData, loading: shipnodeLoading } =
    WarehouseShipnodeHook(warehouseId);

  const [computerOptions, setComputerOptions] = useState([]);
  const [printerOptions, setPrinterOptions] = useState([]);

  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(WarehouseSchema),
  });

  const computerId = watch('computerId');

  const renderOption = (name, offline) => (
    <div className="d-flex align-items-center">
      <span>{name}</span>
      {offline ? (
        <ColoredPill color={theme.colors.red} className="ml-2">
          Offline
        </ColoredPill>
      ) : (
        <ColoredPill color={theme.colors.green} className="ml-2">
          Online
        </ColoredPill>
      )}
    </div>
  );
  useEffect(() => {
    /* Paired ShipNode stations (EN-3274); the 2023 apps' Firestore computers were retired in EN-3208. */
    const stations = normalizeShipnodeEntries(shipnodeData);
    if (stations.length) {
      const availableComputers = stations.map(station => {
        const printers = station.printers.map(printer => ({
          text: renderOption(printer.name, !printer.online),
          content: renderOption(printer.name, !printer.online),
          value: printer.id,
          key: printer.id,
        }));

        return {
          text: renderOption(station.name, !station.online),
          content: renderOption(station.name, !station.online),
          value: station.id,
          key: station.id,
          printers,
        };
      });

      const computerOptions = availableComputers.filter(
        shipnode =>
          shipnode.printers.length > 0 &&
          !existingShipnodes.find(x => x.computerId === shipnode.value),
      );

      setComputerOptions(computerOptions);
      if (computerOptions?.length === 1) {
        setValue('computerId', computerOptions[0].value);
      }
    } else {
      setComputerOptions([]);
      setValue('computerId', '');
    }
  }, [shipnodeData, existingShipnodes]);

  useEffect(() => {
    if (computerId && CommonUtility.isValidArray(computerOptions)) {
      const printerOptions = computerOptions.find(
        computer => computer.value === computerId,
      )?.printers;

      setPrinterOptions(printerOptions);
      if (printerOptions?.length) {
        setValue('printerId', printerOptions[0].value);
      }
    } else {
      setValue('printerId', '');
      setPrinterOptions([]);
    }
  }, [computerId, computerOptions]);

  useEffect(() => {
    if (!open) {
      reset({
        printerId: '',
        computerId: '',
        stationName: '',
      });
    }
  }, [open]);

  return (
    <SkupremeModal open={open} onClose={onClose} title="Add Packing Stations" size="mini">
      <Form onSubmit={handleSubmit(savePacking)}>
        <ControlledTextFormField
          control={control}
          name="stationName"
          error={errors.stationName}
          label="Station Name"
          hint={errors.stationName?.message}
        />
        <DropdownFormField
          control={control}
          name="computerId"
          error={errors.computerId}
          label="Computer Id"
          hint={errors.computerId?.message}
          options={computerOptions}
          loading={shipnodeLoading}
        />
        <DropdownFormField
          control={control}
          name="printerId"
          error={errors.printerId}
          label="Printer Id"
          hint={errors.printerId?.message}
          options={printerOptions}
          loading={shipnodeLoading}
        />
        <div className="d-flex justify-content-end">
          <HoverBorderButton onClick={onClose} disabled={saving} type="button">
            Cancel
          </HoverBorderButton>
          <StyledButton loading={saving} disabled={!isValid} className="ml-2">
            Confirm
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}
